import { FC, useEffect, useState, ReactNode } from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { useAuthStore } from '../../../../store/authStore'
import { useNavigate } from 'react-router-dom'
import { useUserProfile, useUserPermissions, useCurrentOrganization } from '../../../../hooks/queries'
import { queryClient } from '../../../../lib/react-query/queryClient'
import { userApi } from '../../../../services/api'
import { useUserData } from '../../../../hooks/queries/useUser'
import { resolveAuthBootstrap } from './authBootstrapState'

type Props = { children: ReactNode }

const AuthInit: FC<Props> = (props) => {
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const [authCheckComplete, setAuthCheckComplete] = useState(false)
  const setDataPermissionList = useAuthStore((state) => state.SetDataPermissionList)
  const navigate = useNavigate()

  // Fetch user profile using React Query
  const {
    data: userData,
    isLoading: userLoading,
    isError: userError,
    error: userErrorData
  } = useUserProfile()
  // Resolve the current organization. This sets orgId in the auth store, which
  // un-gates the permissions query and attaches the X-Current-Organization
  // header. It must be mounted here (above the splash gate) so the org resolves
  // before the children — which is where the org is otherwise first fetched.
  const {
    isError: orgError,
    error: orgErrorData
  } = useCurrentOrganization()
  // Fetch the user-profile (/user-profile/me/) so the auth store's `userProfile`
  // (is_admin / requires_organization_setup) is populated. The hook syncs itself
  // to Zustand; mounting it here keeps it above the splash gate alongside the org.
  useUserData()
  // Fetch permissions using React Query. Gated on orgId, so it waits for the
  // org above to resolve before firing.
  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    isError: permissionsError,
    error: permissionsErrorData
  } = useUserPermissions()

  // Sync permissions to Zustand when they arrive
  useEffect(() => {
    if (permissionsData?.permissions) {
      setDataPermissionList(permissionsData.permissions)
    }
  }, [permissionsData, setDataPermissionList])

  // Effect to check authentication on auth pages
  useEffect(() => {
    const isAuthPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth')

    if (!isAuthPage) {
      // Not on auth page, auth check not needed
      setAuthCheckComplete(true)
      return
    }

    // On auth page - validate authentication using permissions endpoint
    async function checkAuth() {
      try {
        // Use permissions endpoint to validate auth and get permissions in one call
        const permissionsData = await userApi.getPermissions()

        // User is authenticated - now fetch user profile data
        try {
          const userPData = await userApi.getUserData()
          useAuthStore.getState().UserProfile(userPData)
          const userData = await userApi.getProfile()
          useAuthStore.getState().UserLoaded(userData)
        } catch (err) {
          // User profile fetch failed but permissions succeeded
          // Continue with permissions only (fail-safe)
          console.warn('Failed to fetch user profile during auth check:', err)
        }

        // Load permissions into Zustand
        setDataPermissionList(permissionsData.permissions)

        // Redirect to main route
        // Note: We need to get the main route from feature flag engine
        // Since useFeature is a hook and can't be called here, we'll use a fallback
        // The main route will be recalculated when the app initializes after redirect
        navigate('/dashboard', { replace: true })

        setAuthCheckComplete(true)

      } catch (error: any) {
        // Not authenticated (401) or network error
        // CRITICAL: Mark check as complete to prevent infinite loops
        // The axios interceptor already handles token refresh attempts
        // If we get here, it means:
        // - 401: No valid session (expected - show login)
        // - Network error: Can't reach server (show login as fail-safe)
        setAuthCheckComplete(true)
      }
    }

    checkAuth()
  }, [navigate, setDataPermissionList])

  // Main initialization effect
  useEffect(() => {
    // Check if we're on an auth page (login, signup, etc.)
    const isAuthPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth')

    // If on auth page, wait for manual auth check to complete
    if (isAuthPage) {
      if (authCheckComplete) {
        setShowSplashScreen(false)
      }
      return
    }

    // A first-login user has no organization yet: /organizations/current/
    // returns 404 and the profile carries requires_organization_setup. That 404
    // is expected — it must NOT log the user out, and the org-scoped permissions
    // query can never resolve without an org, so the splash gate must not wait
    // for it. resolveAuthBootstrap encodes those cases (see authBootstrapState).
    const orgErrorStatus = orgError ? (orgErrorData as any)?.response?.status : undefined

    const decision = resolveAuthBootstrap({
      userLoading,
      permissionsLoading,
      hasUserData: !!userData,
      hasPermissionsData: !!permissionsData,
      requiresOrgSetup: !!userData?.requires_organization_setup,
      userError: !!userError,
      permissionsError: !!permissionsError,
      orgErrorStatus,
    })

    if (decision === 'logout') {
      // Genuine authentication/bootstrap failure (401, non-404 org error, etc.)
      console.error('Auth initialization failed:', {
        userError: userErrorData,
        permissionsError: permissionsErrorData,
        orgError: orgErrorData
      })

      // Cancel all ongoing queries
      queryClient.cancelQueries()
      // Clear React Query cache
      queryClient.clear()
      // Clear Zustand store and logout
      useAuthStore.getState().Logout_(false)

      // Hide splash screen to show login page
      setShowSplashScreen(false)
      return
    }

    if (decision === 'ready' || decision === 'setup') {
      // 'ready'  → fully bootstrapped, render the app.
      // 'setup'  → authenticated but no org yet; render so MasterLayout can show
      //            the Create-Organization gate (InitialOrg).
      setShowSplashScreen(false)
    }
  }, [
    authCheckComplete,
    userLoading,
    permissionsLoading,
    userData,
    permissionsData,
    userError,
    permissionsError,
    orgError,
    userErrorData,
    permissionsErrorData,
    orgErrorData,
    navigate
  ])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{props.children}</>
}

export default AuthInit
